import { useState } from 'react';
import {
  signInWithGoogle,
  signInWithMagicLink,
  signInWithPassword,
  signUpWithPassword,
} from '../../lib/supabase.js';

function GoogleIcon() {
  return (
    <svg viewBox="0 0 48 48" className="h-5 w-5" aria-hidden="true">
      <path
        fill="#FFC107"
        d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"
      />
      <path
        fill="#FF3D00"
        d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"
      />
      <path
        fill="#4CAF50"
        d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"
      />
      <path
        fill="#1976D2"
        d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571.001-.001.002-.001.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"
      />
    </svg>
  );
}

export default function AuthPanel({ mode: initialMode = 'signin', nextPath = '/dashboard/jobs', onAuthed }) {
  const [mode, setMode] = useState(initialMode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [googleSubmitting, setGoogleSubmitting] = useState(false);
  const [magicSubmitting, setMagicSubmitting] = useState(false);

  const isSignup = mode === 'signup';

  async function handleGoogle() {
    setError('');
    setInfo('');
    setGoogleSubmitting(true);
    const { error: oauthError } = await signInWithGoogle(nextPath);
    if (oauthError) {
      setError(oauthError.message);
      setGoogleSubmitting(false);
    }
    // OAuth navigates away — no follow-up needed.
  }

  async function handleEmailPassword(event) {
    event.preventDefault();
    setError('');
    setInfo('');
    if (!email || !password) {
      setError('Email and password are required.');
      return;
    }
    if (isSignup && password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    setSubmitting(true);
    const action = isSignup ? signUpWithPassword : signInWithPassword;
    const { data, error: authError } = await action(email, password);
    setSubmitting(false);
    if (authError) {
      setError(authError.message);
      return;
    }
    if (isSignup && !data?.session) {
      setInfo('Check your email to confirm your account, then sign in.');
      return;
    }
    if (typeof onAuthed === 'function') {
      onAuthed();
    }
  }

  async function handleMagicLink() {
    setError('');
    setInfo('');
    if (!email) {
      setError('Enter your email first, then tap "Email me a sign-in link".');
      return;
    }
    setMagicSubmitting(true);
    const { error: magicError } = await signInWithMagicLink(email, nextPath);
    setMagicSubmitting(false);
    if (magicError) {
      setError(magicError.message);
      return;
    }
    setInfo('Check your inbox for the sign-in link.');
  }

  return (
    <div className="w-full">
      <button
        type="button"
        onClick={handleGoogle}
        disabled={googleSubmitting}
        className="flex h-14 w-full items-center justify-center gap-3 rounded-[10px] bg-teal-600 px-5 text-base font-bold text-white shadow-lg shadow-teal-900/15 transition hover:bg-teal-700 focus:outline-none focus:ring-4 focus:ring-teal-200 disabled:cursor-not-allowed disabled:opacity-70"
      >
        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-white">
          <GoogleIcon />
        </span>
        {googleSubmitting ? 'Redirecting…' : 'Continue with Google'}
      </button>

      <div className="my-5 flex items-center gap-3" aria-hidden="true">
        <span className="h-px flex-1 bg-slate-200" />
        <span className="text-xs font-bold uppercase tracking-[0.16em] text-slate-400">or continue with email</span>
        <span className="h-px flex-1 bg-slate-200" />
      </div>

      <form className="space-y-3" onSubmit={handleEmailPassword} noValidate>
        <div>
          <label htmlFor="auth-email" className="block text-xs font-bold uppercase tracking-[0.12em] text-slate-500">
            Email
          </label>
          <input
            id="auth-email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            className="mt-1 h-12 w-full rounded-[8px] border border-slate-200 bg-white px-4 text-sm focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-200"
            placeholder="you@example.com"
          />
        </div>
        <div>
          <label htmlFor="auth-password" className="block text-xs font-bold uppercase tracking-[0.12em] text-slate-500">
            Password
          </label>
          <input
            id="auth-password"
            type="password"
            autoComplete={isSignup ? 'new-password' : 'current-password'}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            className="mt-1 h-12 w-full rounded-[8px] border border-slate-200 bg-white px-4 text-sm focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-200"
            placeholder={isSignup ? 'At least 8 characters' : 'Your password'}
          />
        </div>

        {error ? (
          <p className="rounded-[8px] border border-coral-200 bg-coral-50 px-3 py-2 text-sm text-coral-700">{error}</p>
        ) : null}
        {info ? (
          <p className="rounded-[8px] border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">{info}</p>
        ) : null}

        <button
          type="submit"
          disabled={submitting}
          className="h-12 w-full rounded-[8px] bg-obsidian-400 px-5 text-sm font-bold text-white shadow-lg shadow-slate-900/15 transition hover:bg-slate-900 focus:outline-none focus:ring-4 focus:ring-teal-200 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {submitting
            ? isSignup
              ? 'Creating account…'
              : 'Signing in…'
            : isSignup
              ? 'Create account'
              : 'Sign in'}
        </button>
      </form>

      <div className="mt-4 flex flex-col gap-2 text-sm">
        <button
          type="button"
          onClick={() => {
            setError('');
            setInfo('');
            setMode(isSignup ? 'signin' : 'signup');
          }}
          className="text-left font-bold text-teal-700 hover:text-teal-800 focus:outline-none focus:underline"
        >
          {isSignup ? 'Already have an account? Sign in' : "Don't have an account? Sign up"}
        </button>
        <button
          type="button"
          onClick={handleMagicLink}
          disabled={magicSubmitting}
          className="text-left text-slate-600 hover:text-slate-900 focus:outline-none focus:underline disabled:opacity-60"
        >
          {magicSubmitting ? 'Sending link…' : 'Email me a sign-in link'}
        </button>
      </div>
    </div>
  );
}
